import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import Lightbox from '../components/Lightbox';
import { imageUrl } from '../lib/images';

export default function GalleryPage() {
  const [galleries, setGalleries] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const [loading, setLoading] = useState(true);

  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [activeImageList, setActiveImageList] = useState([]);

  useEffect(() => {
    fetch('/_api/public/content/galleri')
      .then((res) => res.json())
      .then((data) => {
        const items = Array.isArray(data) ? data : data.items || [];
        setGalleries(items);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Fejl ved indlæsning af gallerier:', err);
        setLoading(false);
      });
  }, []);

  // Collect all photos or filter by category
  const filteredGalleries = selectedCategory === 'Alle'
    ? galleries
    : galleries.filter((g) => g.category?.toLowerCase() === selectedCategory.toLowerCase() || g.title?.toLowerCase() === selectedCategory.toLowerCase());

  // Flatten images for the current view
  const allImages = [];
  filteredGalleries.forEach((gal) => {
    if (Array.isArray(gal.images)) {
      gal.images.forEach((img) => {
        allImages.push({
          url: img,
          title: gal.title,
          category: gal.category
        });
      });
    }
  });

  const categories = ['Alle', 'Autotransport', 'Autohjælp', 'Bjærgning', 'Bugsering', 'Flåde / vores biler'];

  const handleOpenImage = (idx) => {
    setActiveImageList(allImages.map((i) => i.url));
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  return (
    <>
      <SEOHead
        title="Galleri | Vores røde lastbiler i aktion | MC Transport"
        description="Se billeder af MC Transports vognpark, autotransportere, bjærgningskøretøjer og opgaver i hele Danmark. Åbn billeder i fuld skærm."
      />

      {/* Hero */}
      <section className="bg-zinc-950 text-white py-16 lg:py-20 border-b border-zinc-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(220,38,38,0.2),transparent_70%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-red-500 font-condensed mb-2 block">
            Billeder fra hverdagen
          </span>
          <h1 className="font-condensed text-4xl sm:text-6xl font-extrabold uppercase tracking-tight text-white">
            Galleri
          </h1>
          <p className="mt-4 text-base sm:text-lg text-zinc-300 max-w-2xl mx-auto">
            Gå på opdagelse i vores galleri og se billeder af <Link to="/" className="font-semibold text-white hover:text-red-400 underline decoration-red-500 underline-offset-2 transition-colors">MC Transport</Link>s røde vognpark – herunder <Link to="/ydelser/autotransport-ydelser" className="text-zinc-200 hover:text-white underline decoration-zinc-400 underline-offset-2">autotransportere</Link>, Scania <Link to="/bjaergning" className="text-zinc-200 hover:text-white underline decoration-zinc-400 underline-offset-2">bjærgere</Link> og Uni Man 4x4 til <Link to="/fritraekning" className="text-zinc-200 hover:text-white underline decoration-zinc-400 underline-offset-2">fritrækning</Link> i aktion over hele Danmark.
          </p>

          {/* Category Filter Pills */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded text-xs sm:text-sm font-bold uppercase tracking-wider transition-colors ${
                  selectedCategory.toLowerCase() === cat.toLowerCase()
                    ? 'bg-red-600 text-white shadow'
                    : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Image Grid */}
      <section className="py-16 bg-zinc-100 min-h-[500px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20 text-zinc-500 font-mono text-sm">
              <i className="fa-solid fa-spinner fa-spin text-2xl mb-2 text-red-600" aria-hidden="true" />
              <p>Henter galleri fra CMS...</p>
            </div>
          ) : allImages.length === 0 ? (
            <div className="text-center py-16 text-zinc-500">
              <p>Ingen billeder fundet i denne kategori.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {allImages.map((imgObj, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleOpenImage(idx)}
                  className="group relative aspect-4/3 rounded-lg overflow-hidden bg-zinc-950 border border-zinc-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-600"
                >
                  <img
                    src={imageUrl(imgObj.url, 'width=600,height=450,fit=cover,quality=80,format=auto')}
                    alt={`${imgObj.title} - MC Transport`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 brightness-95"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3 text-left">
                    <span className="text-[10px] uppercase font-bold text-red-400">{imgObj.category}</span>
                    <span className="text-xs font-semibold text-white truncate">{imgObj.title}</span>
                  </div>
                  <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="fa-solid fa-expand text-xs" aria-hidden="true" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox with Touch/Swipe */}
      <Lightbox
        images={activeImageList}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onSelectIndex={(idx) => setLightboxIndex(idx)}
      />
    </>
  );
}
